import { Point3D } from './types';

/**
 * 把局部坐标点 (x,y) 绕原点按角度 heading 旋转到世界坐标系
 * 得到的是“在车辆朝向 yaw 下，局部向前 0.2 的世界坐标偏移”
 *
 * @param {number} x
 * @param {number} y
 * @param {number} heading
 * @return {*}
 */
export function rotatePoint(x: number, y: number, heading: number) {
  const xNew = x * Math.cos(heading) - y * Math.sin(heading);
  const yNew = x * Math.sin(heading) + y * Math.cos(heading);
  return { x: xNew, y: yNew };
}

/**
 * 计算多边形顶点的几何中心
 * @param pts 顶点数组 [{x, y}, ...]
 */
export function getCenter(pts: any[]) {
  let sumX = 0;
  let sumY = 0;
  const len = pts.length;

  if (len === 0) return { x: 0, y: 0 };

  for (let i = 0; i < len; i++) {
    sumX += pts[i].x;
    sumY += pts[i].y;
  }

  return {
    x: sumX / len,
    y: sumY / len,
  };
}

function distance(p1: number[], p2: number[]): number {
  return Math.sqrt(Math.pow(p2[0] - p1[0], 2) + Math.pow(p2[1] - p1[1], 2));
}
function midpoint(p1: number[], p2: number[]): number[] {
  return [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
}

export function getTailLightPosition(points: number[][]): number[] {
  const edges = [
    {
      points: [points[0], points[1]],
      length: distance(points[0], points[1]),
    },
    {
      points: [points[1], points[2]],
      length: distance(points[1], points[2]),
    },
    {
      points: [points[2], points[3]],
      length: distance(points[2], points[3]),
    },
    {
      points: [points[3], points[0]],
      length: distance(points[3], points[0]),
    },
  ];
  edges.sort((a, b) => a.length - b.length);
  const shortestEdges = edges.slice(0, 2);
  const midpoints = shortestEdges.map((edge) =>
    midpoint(edge.points[0], edge.points[1])
  );
  let closestMidpoint = midpoints[0];
  let closestDistance = distance([0, 0], midpoints[0]);
  midpoints.forEach((midpoint) => {
    const d = distance([0, 0], midpoint);
    if (d < closestDistance) {
      closestDistance = d;
      closestMidpoint = midpoint;
    }
  });
  return closestMidpoint;
}

/**
 * 增加 zOffset 参数，防止多条线重叠闪烁
 */
export function getWidePathVertices(
  path: any[],
  width: number,
  zOffset: number = 0
): number[] {
  if (path.length < 2) return [];
  const halfWidth = width / 2;
  const result: number[] = [];

  for (let i = 0; i < path.length; i++) {
    const curr = path[i];
    const next = path[i + 1] || curr;
    let dx = next.x - curr.x;
    let dy = next.y - curr.y;

    if (i === path.length - 1 && i > 0) {
      dx = curr.x - path[i - 1].x;
      dy = curr.y - path[i - 1].y;
    }

    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = (-dy / len) * halfWidth;
    const ny = (dx / len) * halfWidth;

    // 写入 Z 偏移
    const z = (curr.z || 0) + zOffset;
    curr._left = { x: curr.x + nx, y: curr.y + ny, z };
    curr._right = { x: curr.x - nx, y: curr.y - ny, z };
  }

  for (let i = 0; i < path.length - 1; i++) {
    const p1 = path[i];
    const p2 = path[i + 1];
    result.push(
      p1._left.x,
      p1._left.y,
      p1._left.z,
      p1._right.x,
      p1._right.y,
      p1._right.z,
      p2._left.x,
      p2._left.y,
      p2._left.z
    );
    result.push(
      p1._right.x,
      p1._right.y,
      p1._right.z,
      p2._right.x,
      p2._right.y,
      p2._right.z,
      p2._left.x,
      p2._left.y,
      p2._left.z
    );
  }
  return result;
}

// 辅助函数：将路径点对转为 LineSegments 顶点格式 [x1,y1,z1, x2,y2,z2...]
export function pathToLineSegments(
  points: Point3D[] | undefined,
  zOffset: number = 0
): number[] {
  const segments: number[] = [];
  if (!points || points.length === 0) return segments;
  for (let i = 0; i < points.length - 1; i++) {
    // 这里的 z 取原始值 + 偏移量
    const z = (points[i].z || 0) + zOffset;
    const nextZ = (points[i + 1].z || 0) + zOffset;

    segments.push(points[i].x, points[i].y, z);
    segments.push(points[i + 1].x, points[i + 1].y, nextZ);
  }
  return segments;
}

/**
 * 双实线/双虚线边界：按法向把中心线拆成左右两条线。
 * 这里复用 Origin 的几何思路，但输出为 Point3D[]，继续走 worker + meshPool 的合并管线。
 */
export const splitBoundaryToDoublePaths = (
  path: Point3D[] | undefined,
  halfWidth = 0.1
): { left: Point3D[]; right: Point3D[] } => {
  if (!path || path.length < 2) return { left: [], right: [] };

  const left: Point3D[] = [];
  const right: Point3D[] = [];

  for (let i = 0; i < path.length; i++) {
    const p = path[i];

    const from =
      i === 0
        ? p
        : ({
            x: path[i - 1].x,
            y: path[i - 1].y,
            z: path[i - 1].z || 0,
          } as Point3D);
    const to =
      i === path.length - 1
        ? p
        : ({
            x: path[i + 1].x,
            y: path[i + 1].y,
            z: path[i + 1].z || 0,
          } as Point3D);

    let dx = to.x - from.x;
    let dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    dx /= len;
    dy /= len;

    // up(0,0,1) x dir(dx,dy,0) => (-dy, dx, 0)
    const nx = -dy;
    const ny = dx;

    left.push({
      x: p.x + nx * halfWidth,
      y: p.y + ny * halfWidth,
      z: p.z || 0,
    });
    right.push({
      x: p.x - nx * halfWidth,
      y: p.y - ny * halfWidth,
      z: p.z || 0,
    });
  }

  return { left, right };
};

/**
 * 计算适合红绿灯 label 的 DPR（设备像素比）。高 DPR 可以提升文本清晰度，但会增加纹理更新开销和显存占用。
 *
 * @param {number} maxDrp
 * @return {*}
 */
export const getLabelDpr = (maxDrp: number) => {
  try {
    const dpr =
      typeof window !== 'undefined' &&
      typeof window.devicePixelRatio === 'number'
        ? window.devicePixelRatio
        : 1;
    return Math.max(1, Math.min(maxDrp, dpr));
  } catch (_) {
    return 1;
  }
};
